// this is the sign in / create account page
import React, { useState } from 'react';
// usenavigate lets us send people to another page in code (after they log in etc)
// usesearchparams reads the ?mode=signup bit off the url so a "Sign up" button
// elsewhere on the site can open this page on the right tab
// link is the router's version of <a> - it swaps the page in without a full reload
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
// our shared supabase client that does all the auth work
import { supabase } from '../supabaseClient';
// our custom icon component
import Icon from '../components/Icon';
// gives this route its own <title> and description instead of the homepage's
import { usePageMeta } from '../hooks/usePageMeta';
// the objects that hold our validation rules - one per field
import { LIMITS, nameValidator, emailValidator, passwordValidatorFor } from '../validators';
// styles just for this page
import './AuthPage.css';

// --- validation rules ---
// the rules themselves live in ../validators as a small class hierarchy, so the
// forms and the rules can be read (and changed) separately. the three functions
// below are the way the pages reach them: they hand a value to the right
// validator and pass the message back. a function returns '' when the value is
// fine, or the message to show if not.

// re-exported so the forgot-password page keeps importing the limits from the
// same place it always has. it's imported above rather than re-exported straight
// from '../validators', because a bare `export { LIMITS } from ...` forwards the
// name to importers without binding it locally - the JSX below would then blow
// up on an undefined LIMITS the moment the sign-up tab rendered.
export { LIMITS };

// name is only asked for on the sign-up tab
export function validateName(value) {
  return nameValidator.validate(value);
}

export function validateEmail(value) {
  return emailValidator.validate(value);
}

// which policy applies depends on the tab - the sign-up tab gets the full set
// of rules, the sign-in tab only the ones that can't lock out an existing
// account. passwordValidatorFor picks the right one.
export function validatePassword(value, tab) {
  return passwordValidatorFor(tab).validate(value);
}

// takes whatever supabase handed back and turns it into something worth reading.
// the one we really care about is "email not confirmed" - supabase just says
// "Email not confirmed", which doesn't tell you what to actually do about it, so
// we point them at the inbox we already sent the link to. anything we don't
// recognise gets passed through as-is rather than swallowed behind a vague
// "something went wrong".
function describeAuthError(error, email) {
  // supabase-js v2 gives a stable `code`; fall back to matching the text in
  // case an older/newer response doesn't carry one
  const code = error?.code || '';
  const message = error?.message || 'Something went wrong. Please try again.';

  if (code === 'email_not_confirmed' || /email not confirmed/i.test(message)) {
    // note the past tense - this link was sent when they signed up, we aren't
    // sending a fresh one here, so don't imply we just did
    return `Your email address hasn't been confirmed yet. A confirmation email has been sent to ${email} - check your inbox (and your spam folder) and click the link, then sign in.`;
  }

  return message;
}

// runs every checker that applies to the current tab and hands back an object
// like { email: 'Please enter your email address.' }. an empty object means
// everything passed and we're clear to talk to the server.
function validateForm({ tab, name, email, password }) {
  const errors = {};

  if (tab === 'signup') {
    // name is optional - only check it if they actually filled it in
    const nameError = name.trim() === '' ? '' : validateName(name);
    if (nameError) errors.name = nameError;
  }

  const emailError = validateEmail(email);
  if (emailError) errors.email = emailError;

  const passwordError = validatePassword(password, tab);
  if (passwordError) errors.password = passwordError;

  return errors;
}


export default function AuthPage() {
  usePageMeta({
    title: 'Sign in or create an account - GPTDisguise',
    description: 'Sign in to GPTDisguise to manage your Premium plan, or create a free account. The free Google Docs disguise works without an account.',
    path: '/signin'
  });

  // /signin?mode=signup opens straight on the Create account tab, so the "Sign up"
  // button in the landing nav lands people where it says it will
  const [searchParams] = useSearchParams();

  // all these useState bits are little pieces of memory for the form.
  // 'tab' tracks whether we're showing the sign-in tab or the sign-up tab
  const [tab, setTab] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  // the name field (only shown when signing up)
  const [name, setName] = useState('');
  // the email the user types
  const [email, setEmail] = useState('');
  // the password the user types
  const [password, setPassword] = useState('');
  // whether we're mid-request, so we can disable the button and show "please wait"
  const [loading, setLoading] = useState(false);
  // an error message to show in red if something goes wrong
  const [error, setError] = useState('');
  // a success message to show in green
  const [success, setSuccess] = useState('');
  // per-field validation messages, e.g. { email: 'Please enter your email address.' }
  // these show up underneath the input they belong to
  const [fieldErrors, setFieldErrors] = useState({});
  // lets us redirect after a successful login
  const navigate = useNavigate();

  // wipes every message on screen - used when switching tabs so old complaints
  // about a field you can't even see anymore don't stick around
  const clearMessages = () => {
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  // as soon as someone starts fixing a field, drop that field's error. leaving
  // it there while they're mid-correction just feels like nagging.
  const clearFieldError = field => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // check a single field the moment they click away from it, so problems get
  // caught one at a time instead of all at once when they hit submit
  const handleBlur = (field, value) => {
    const message =
      // blank name is allowed, so don't complain about one on the way out
      field === 'name' ? (value.trim() === '' ? '' : validateName(value))
      : field === 'email' ? validateEmail(value)
      : validatePassword(value, tab);
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  };

  // this runs when the form is submitted - by clicking the button, by pressing
  // enter in any of the fields, or by the "Go" key on a phone keyboard. it's on
  // the <form> rather than on one input, which is what makes all three work.
  const handleSubmit = async (event) => {
    // stop the browser doing its own full-page form post
    event.preventDefault();

    // clear out any old messages
    setError('');
    setSuccess('');

    // run every check for this tab first. if anything is wrong we show the
    // messages and stop here - no point bothering the server with input we
    // already know it's going to reject.
    const errors = validateForm({ tab, name, email, password });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // input looks good, so now we're actually going to make a request
    setLoading(true);

    // send the tidied-up versions rather than exactly what was typed, so a
    // stray space at the end of an email doesn't create a duplicate account
    const cleanEmail = email.trim();
    const cleanName = name.trim();

    // branch depending on which tab we're on
    if (tab === 'signup') {
      // creating a brand new account - also stash their name in the user metadata
      const { error } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: { data: { full_name: cleanName } }
      });
      if (error) {
        // signup failed, show why
        setError(describeAuthError(error, cleanEmail));
      } else {
        // supabase sends a confirmation email, so tell them exactly where it went
        setSuccess(`Account created - we've sent a confirmation email to ${cleanEmail}. Check your inbox (and your spam folder) and click the link, then come back and sign in.`);
      }
    } else {
      // otherwise we're just signing an existing user in
      const { data, error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
      if (error) {
        setError(describeAuthError(error, cleanEmail));
      } else {
        // logged in! send them straight to their dashboard
        navigate('/dashboard');
      }
    }

    // whatever happened, we're done loading now
    setLoading(false);
  };

  // this handles the "continue with google" button instead of email/password
  const handleGoogle = async () => {
    // google handles its own validation, so clear anything our form was showing
    clearMessages();
    // kick off google's oauth popup/redirect flow
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      // after google says ok, bounce them back to our dashboard
      options: { redirectTo: `${window.location.origin}/dashboard` }
    });
    if (error) setError(error.message);
  };

  // everything below is the actual html (jsx) that gets drawn on screen
  return (
    <div className="auth-page">
      {/* these two orbs are just blurry glowing blobs in the background for looks */}
      <div className="auth-bg-orb auth-bg-orb--a" />
      <div className="auth-bg-orb auth-bg-orb--b" />

      {/* little back arrow up top that takes you home */}
      <button type="button" className="auth-back" onClick={() => navigate('/')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back
      </button>

      {/* the whole layout is split into a left "sales pitch" side and a right "form" side */}
      <div className="auth-wrap">
        {/* LEFT SIDE - branding and reasons to sign up */}
        <div className="auth-left">
          <div className="auth-brand">
            <img src="/icon.png" alt="GPTDisguise" className="auth-brand-icon" />
            <span className="auth-brand-name">GPT<em>Disguise</em></span>
          </div>
          <h2 className="auth-left-headline">
            Use ChatGPT<br />
            <span>in plain sight.</span>
          </h2>
          <p className="auth-left-sub">
            Join thousands of people who use GPTDisguise to stay productive - and undercover.
          </p>
          {/* the little row of colored avatar dots + "5k+ users" social proof */}
          <div className="auth-social-proof">
            <div className="proof-avatars">
              {/* loop over 4 google-ish colors and make a little dot for each */}
              {['#4285f4','#34a853','#ea4335','#fbbc04'].map((c, i) => (
                <div key={i} className="proof-avatar" style={{ background: c, zIndex: 4 - i }} />
              ))}
            </div>
            <span> 5k+ users and counting</span>
          </div>
          {/* three quick selling-point bullets, each with a green check */}
          <div className="auth-features-mini">
            {['Google Docs disguise', 'Works on any page', 'Nothing leaves your browser'].map(f => (
              <div key={f} className="auth-feature-mini-item">
                <span className="auth-check"><Icon name="check" size={12} strokeWidth={2.2} /></span>{f}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE - the actual login/signup card */}
        <div className="auth-right">
          <div className="auth-card">
            {/* the two tabs at the top to flip between sign in and create account */}
            <div className="auth-tabs">
              {/* type="button" on every button that isn't the submit one: inside
                  or beside a form, a button with no type defaults to submit and
                  would fire the form off when clicked */}
              <button type="button" className={`auth-tab ${tab === 'signin' ? 'active' : ''}`} onClick={() => { setTab('signin'); clearMessages(); }}>
                Sign in
              </button>
              <button type="button" className={`auth-tab ${tab === 'signup' ? 'active' : ''}`} onClick={() => { setTab('signup'); clearMessages(); }}>
                Create account
              </button>
            </div>

            {/* the google button - its text changes a bit depending on the tab */}
            <button type="button" className="google-btn" onClick={handleGoogle}>
              <GoogleIcon />
              {tab === 'signin' ? 'Continue' : 'Sign up'} with Google
            </button>

            {/* the little "or" divider between google and the email form */}
            <div className="auth-or"><span>or</span></div>

            {/* everything the user types lives inside a real <form>. that's what
                makes enter-to-submit work from every field instead of just the
                password box, and it's what lets password managers and the "Go"
                key on phone keyboards recognise this as a login form.
                novalidate switches off the browser's own popup bubbles so our
                messages are the only ones the user has to read. */}
            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              {/* only show the name field when they're creating an account */}
              {tab === 'signup' && (
                <div className="auth-field">
                  {/* htmlfor points at the input's id. without it the label is just
                      text sitting nearby - clicking the word wouldn't focus the box
                      and a screen reader would read the field out as unlabelled. */}
                  <label htmlFor="auth-name">
                    Name
                    <span className="field-optional">Optional</span>
                  </label>
                  <input
                    id="auth-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Your name"
                    value={name}
                    // no maxlength, same reason as the email field below
                    className={fieldErrors.name ? 'has-error' : ''}
                    aria-invalid={fieldErrors.name ? 'true' : undefined}
                    aria-describedby={fieldErrors.name ? 'auth-name-error' : undefined}
                    onChange={e => { setName(e.target.value); clearFieldError('name'); }}
                    onBlur={e => handleBlur('name', e.target.value)}
                  />
                  {/* the field's own error sits right under it so it's obvious which one is wrong.
                      role="alert" is what makes a screen reader speak it - without
                      it the message appears silently and only sighted users learn
                      anything went wrong. */}
                  {fieldErrors.name && <div className="auth-field-error" id="auth-name-error" role="alert">{fieldErrors.name}</div>}
                </div>
              )}
              {/* email input - value + onchange keep our state in sync with what they type */}
              <div className="auth-field">
                <label htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  // no maxlength on purpose. with one, the browser silently chops
                  // off anything past the limit, so someone pasting a long address
                  // just watches it get shortened with no explanation. validateEmail
                  // enforces the same limit and actually tells them why.
                  className={fieldErrors.email ? 'has-error' : ''}
                  aria-invalid={fieldErrors.email ? 'true' : undefined}
                  aria-describedby={fieldErrors.email ? 'auth-email-error' : undefined}
                  onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
                  onBlur={e => handleBlur('email', e.target.value)}
                />
                {fieldErrors.email && <div className="auth-field-error" id="auth-email-error" role="alert">{fieldErrors.email}</div>}
              </div>
              {/* password input, with a "forgot?" link that only appears on the sign-in tab */}
              <div className="auth-field">
                {/* the link sits beside the label rather than inside it: a click
                    on a <label> gets forwarded to its input, which fights with a
                    link trying to navigate */}
                <div className="auth-label-row">
                  <label htmlFor="auth-password">Password</label>
                  {tab === 'signin' && <Link to="/forgot" className="forgot-link">Forgot?</Link>}
                </div>
                <input
                  id="auth-password"
                  type="password"
                  // tells a password manager whether to offer a saved password or
                  // to generate a new one
                  autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
                  placeholder={tab === 'signup' ? `At least ${LIMITS.passwordMin} characters` : '••••••••'}
                  value={password}
                  // no maxlength, same reason as the email field above
                  className={fieldErrors.password ? 'has-error' : ''}
                  aria-invalid={fieldErrors.password ? 'true' : undefined}
                  aria-describedby={
                    fieldErrors.password ? 'auth-password-error'
                    : tab === 'signup' ? 'auth-password-hint'
                    : undefined
                  }
                  onChange={e => { setPassword(e.target.value); clearFieldError('password'); }}
                  onBlur={e => handleBlur('password', e.target.value)}
                />
                {fieldErrors.password && <div className="auth-field-error" id="auth-password-error" role="alert">{fieldErrors.password}</div>}
                {/* on sign-up, spell out the rules up front rather than making them guess */}
                {tab === 'signup' && !fieldErrors.password && (
                  <div className="auth-field-hint" id="auth-password-hint">At least {LIMITS.passwordMin} characters, including a letter and a number.</div>
                )}
              </div>

              {/* show the red error or green success message if there is one.
                  both are announced, since either one can be the only feedback
                  a user gets after pressing the button. */}
              {error && <div className="auth-message auth-message--error" role="alert">{error}</div>}
              {success && <div className="auth-message auth-message--success" role="status">{success}</div>}

              {/* the big submit button - disabled + shows "please wait" while loading.
                  type="submit" is what connects it to the form's onSubmit above. */}
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Please wait...' : tab === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            {/* a text link at the bottom to quickly jump to the other tab */}
            <p className="auth-switch">
              {tab === 'signin' ? "Don't have an account? " : 'Already have an account? '}
              <button type="button" className="auth-switch-btn" onClick={() => { setTab(tab === 'signin' ? 'signup' : 'signin'); clearMessages(); }}>
                {tab === 'signin' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// the official multi-colored google "G" logo, just hardcoded as an svg so we don't
// need to load an image. it's its own little component to keep the jsx above tidy.
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
